import React from 'react';
import { Link } from 'react-router';
import blackLogo from "../../src/black-logo.png";

import { 
  Instagram, 
  Linkedin, 
  Twitter, 
  Facebook, 
  Mail, 
  Phone, 
  MapPin, 
  ArrowUp,
  MessageCircle
} from 'lucide-react';
import { FaFacebook, FaInstagram, FaLinkedin, FaTiktok, FaWhatsapp } from 'react-icons/fa';

const Footer = () => {
  const colors = {
    brown: "#A3582A",
    green: "#8C8660",
    beige: "#EFE5C6",
    yellow: "#E9BA67",
    lightGreen: "#E4E2D5",
    gray: "#5E6065",
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const openWhatsApp = () => {
    const phoneNumber = "971566964316";
    const message = encodeURIComponent("🚀 Hi Motasim! I'm interested in premium web development services. Let's discuss my project!");
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-white border-t border-[#E9BA67]/20 relative text-start">
      {/* Decorative elements - hidden on smallest screens */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#A3582A]/20 to-transparent"></div>
      <div className="absolute top-0 left-1/4 w-px h-12 sm:h-16 bg-gradient-to-b from-[#A3582A]/20 to-transparent hidden sm:block"></div>
      <div className="absolute top-0 right-1/4 w-px h-12 sm:h-16 bg-gradient-to-b from-[#A3582A]/20 to-transparent hidden sm:block"></div>

      {/* Main footer content */}
      <div className="container mx-auto px-4 sm:px-6 pt-12 sm:pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-6 lg:gap-8">
          {/* Brand Column */}
          <div className="md:col-span-4">
            {/* Logo */}
            <div className="flex items-center mb-4 sm:mb-6">
              <img src={blackLogo} alt="La Passion" className="h-12 md:h-16" />
            </div>
            
            {/* Tagline */}
            <p className="text-sm sm:text-base mb-5 sm:mb-6 font-futura" style={{ color: colors.gray }}>
              Welcome to Takeaya. Where every stay
              unfolds an expanse of comfort, and
              every property finds its fullest expression.
            </p>
            
            {/* Social Media Links */}
            <div className="flex space-x-3 mb-6 sm:mb-8">
              <a target='_blank' href="https://www.instagram.com/takeayaholidayhomes?igsh=MTk2aWNjM2FiYjZldg%3D%3D&utm_source=qr" className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full border border-[#A3582A]/30 hover:bg-[#A3582A]/5 transition-colors duration-300">
                <FaInstagram className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: colors.brown }} />
              </a>
              <a target='_blank' href="https://www.linkedin.com/company/takeaya-holiday-homes/about/?viewAsMember=true" className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full border border-[#A3582A]/30 hover:bg-[#A3582A]/5 transition-colors duration-300">
                <FaLinkedin className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: colors.brown }} />
              </a>
              <a target='_blank' href="https://www.tiktok.com/@takeayaholidayhomes?is_from_webapp=1&sender_device=pc" className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full border border-[#A3582A]/30 hover:bg-[#A3582A]/5 transition-colors duration-300">
                <FaTiktok className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: colors.brown }} />
              </a>
              <a href="#" className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full border border-[#A3582A]/30 hover:bg-[#A3582A]/5 transition-colors duration-300">
                <FaFacebook className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: colors.brown }} />
              </a>
            </div>
            
            {/* Contact Info */}
            <div className="space-y-3 text-start">
              <div className="flex items-center">
                <Mail className="w-4 h-4 mr-2" style={{ color: colors.yellow }} />
                <a href="mailto:contact@takeaya.com" className="text-sm hover:text-[#A3582A] transition-colors" style={{ color: colors.gray }}>
                  info@takeaya.com
                </a>
              </div>
              <div className="flex items-center">
                <Phone className="w-4 h-4 mr-2" style={{ color: colors.yellow }} />
                <a href="tel:+97143xxxxxx" className="text-sm hover:text-[#A3582A] transition-colors" style={{ color: colors.gray }}>
                  +971 58 810 6718
                </a>
              </div>
              <div className="flex items-start">
                <MapPin className="w-4 h-4 mr-2 mt-0.5" style={{ color: colors.yellow }} />
                <p className="text-sm" style={{ color: colors.gray }}>
                  Downtown Dubai,<br />United Arab Emirates
                  DAMAC Smart Height,<br /> Barsha Height, Dubai
                </p>
              </div>
            </div>
          </div>
          
          {/* Quick Links - Responsive layout */}
          <div className="md:col-span-2 lg:col-span-2">
            <h4 className="text-base sm:text-lg font-medium mb-4 font-ivyMode" style={{ color: colors.brown }}>
              Quick Links
            </h4>
            <ul className="space-y-2 sm:space-y-3 text-start">
              <li>
                <Link to="/" className="text-sm hover:text-[#A3582A] transition-colors flex items-center" style={{ color: colors.gray }}>
                  <span className="w-1 h-1 mr-2 rounded-full" style={{ backgroundColor: colors.yellow }}></span>
                  Home
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-sm hover:text-[#A3582A] transition-colors flex items-center" style={{ color: colors.gray }}>
                  <span className="w-1 h-1 mr-2 rounded-full" style={{ backgroundColor: colors.yellow }}></span>
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/properties" className="text-sm hover:text-[#A3582A] transition-colors flex items-center" style={{ color: colors.gray }}>
                  <span className="w-1 h-1 mr-2 rounded-full" style={{ backgroundColor: colors.yellow }}></span>
                  Properties
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-sm hover:text-[#A3582A] transition-colors flex items-center" style={{ color: colors.gray }}>
                  <span className="w-1 h-1 mr-2 rounded-full" style={{ backgroundColor: colors.yellow }}></span>
                  Services
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Services Links */}
          <div className="md:col-span-2 lg:col-span-2">
            <h4 className="text-base sm:text-lg font-medium mb-4 font-ivyMode" style={{ color: colors.brown }}>
              Our Services
            </h4>
            <ul className="space-y-2 sm:space-y-3 text-start">
              <li>
                <Link to="/services#vacation-rentals" className="text-sm hover:text-[#A3582A] transition-colors flex items-center" style={{ color: colors.gray }}>
                  <span className="w-1 h-1 mr-2 rounded-full" style={{ backgroundColor: colors.yellow }}></span>
                  Vacation Homes
                </Link>
              </li>
              <li>
                <Link to="/services#property-management" className="text-sm hover:text-[#A3582A] transition-colors flex items-center" style={{ color: colors.gray }}>
                  <span className="w-1 h-1 mr-2 rounded-full" style={{ backgroundColor: colors.yellow }}></span>
                  Property Management
                </Link>
              </li>
              <li>
                <Link to="/services#interior-styling" className="text-sm hover:text-[#A3582A] transition-colors flex items-center" style={{ color: colors.gray }}>
                  <span className="w-1 h-1 mr-2 rounded-full" style={{ backgroundColor: colors.yellow }}></span>
                  Interior Styling
                </Link>
              </li>
              <li>
                <Link to="/services#maintenance" className="text-sm hover:text-[#A3582A] transition-colors flex items-center" style={{ color: colors.gray }}>
                  <span className="w-1 h-1 mr-2 rounded-full" style={{ backgroundColor: colors.yellow }}></span>
                  Maintenance
                </Link>
              </li>
              <li>
                <Link to="/services#pricing-optimization" className="text-sm hover:text-[#A3582A] transition-colors flex items-center" style={{ color: colors.gray }}>
                  <span className="w-1 h-1 mr-2 rounded-full" style={{ backgroundColor: colors.yellow }}></span>
                  Smart Pricing
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Legal Links */}
          <div className="md:col-span-1 lg:col-span-1">
            <h4 className="text-base sm:text-lg font-medium mb-4 font-ivyMode" style={{ color: colors.brown }}>
              Legal
            </h4>
            <ul className="space-y-2 sm:space-y-3 text-start">
              <li>
                <Link to="/privacy" className="text-sm hover:text-[#A3582A] transition-colors flex items-center" style={{ color: colors.gray }}>
                  <span className="w-1 h-1 mr-2 rounded-full" style={{ backgroundColor: colors.yellow }}></span>
                  Privacy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-sm hover:text-[#A3582A] transition-colors flex items-center" style={{ color: colors.gray }}>
                  <span className="w-1 h-1 mr-2 rounded-full" style={{ backgroundColor: colors.yellow }}></span>
                  Terms
                </Link>
              </li>
              <li>
                <Link to="/cookies" className="text-sm hover:text-[#A3582A] transition-colors flex items-center" style={{ color: colors.gray }}>
                  <span className="w-1 h-1 mr-2 rounded-full" style={{ backgroundColor: colors.yellow }}></span>
                  Cookies
                </Link>
              </li>
            </ul>
          </div>
          
          <div className="md:col-span-3 lg:col-span-3">
            <div className="bg-[#EFE5C6]/20 p-4 rounded-lg border border-[#E9BA67]/20">
              <h5 className="text-sm font-medium mb-2" style={{ color: colors.brown }}>
                Need to speak with us?
              </h5>
              <Link 
                to="/contact" 
                className="text-sm flex items-center font-medium"
                style={{ color: colors.green }}
              >
                Contact our team
                <svg className="w-4 h-4 ml-2" viewBox="0 0 24 24" fill="none">
                  <path d="M14 5l7 7m0 0l-7 7m7-7H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Bottom bar */}
        <div className="mt-10 pt-6 border-t border-[#E9BA67]/20 flex flex-col sm:flex-row justify-between items-center">
          <p className="text-xs sm:text-sm mb-4 sm:mb-0" style={{ color: colors.gray }}>
            © {currentYear} Takeaya. All rights reserved.
          </p>
          
          {/* Back to top button - fixed position on mobile, inline on desktop */}
          <button 
            onClick={scrollToTop}
            className="sm:w-auto flex items-center justify-center text-xs font-medium group"
            style={{ color: colors.brown }}
          >
            <span className="mr-2">Back to top</span>
            <span className="w-6 h-6 bg-[#EFE5C6]/30 rounded-full flex items-center justify-center group-hover:bg-[#EFE5C6]/50 transition-colors duration-300">
              <ArrowUp className="w-3 h-3" />
            </span>
          </button>
        </div>

        {/* Developer Section */}
        <div className="mt-8 pt-6 text-gray-600 font-ivyMode">
          <div className="text-center">
            <p className="text-sm font-medium tracking-wide">
              This website was developed with{' '}
              <span className="text-red-500 text-lg animate-pulse">❤️</span>{' '}
              by{' '}
              <button 
                onClick={openWhatsApp}
                className="font-bold transition-all duration-300 cursor-pointer"
              >
                Motasim Emad
              </button>
            </p>
          </div>
        </div>
      </div>
      
      {/* Fixed back to top button - mobile only */}
      <button 
        onClick={scrollToTop}
        className="fixed sm:hidden bottom-5 right-5 w-10 h-10 rounded-full shadow-lg z-50 flex items-center justify-center"
        style={{ backgroundColor: colors.brown }}
      >
        <ArrowUp className="w-5 h-5 text-white" />
      </button>
    </footer>
  );
};

export default Footer;